import React, { useCallback, useEffect, useRef, useState } from 'react';
import BackButton from '../components/BackButton';

const BG = '#FCF4E4';
const INK = '#111010';
const FONT_BODY = "'Outfit', system-ui, sans-serif";

const PALETTE = ['#5C7FE0', '#7FB8EA', '#8CEBD8', '#C98BF2'];
const GLOWS = [
  'radial-gradient(60% 60% at 50% 30%, rgba(92,127,224,.55), transparent 70%)',
  'radial-gradient(60% 60% at 50% 30%, rgba(127,184,234,.6), transparent 70%)',
  'radial-gradient(60% 60% at 50% 30%, rgba(140,235,216,.6), transparent 70%)',
  'radial-gradient(60% 60% at 50% 30%, rgba(201,139,242,.5), transparent 70%)',
];

const SECTIONS = [
  {
    id: 'ch-1',
    n: 0,
    num: '01',
    tag: 'THE ROLE',
    title: 'Product management for automotive mobile apps',
    body: "I'm a Senior Product Manager at Bethink Labs, steering the North American mobile apps for some of the world's most iconic automotive brands. I build features across iOS and Android for an audience of over a million drivers hitting the road every day.",
    stats: [
      { value: '1M+', label: 'drivers on the apps every day' },
      { value: 'iOS + Android', label: 'shipped in parallel, one roadmap' },
      { value: 'Senior PM', label: 'Bethink Labs · North America' },
    ],
  },
  {
    id: 'ch-2',
    n: 1,
    num: '02',
    tag: 'WIDGETS',
    title: 'Bringing the vehicle to the home screen',
    body: 'I brought iOS widgets to life for our automotive apps, giving drivers instant access to battery range, climate control, and vehicle locks without opening the app. I owned the PRD, partnered closely with design and engineering, and shaped the behaviour across widget sizes and vehicle actions.',
    stats: [
      { value: '3×', label: 'climate-control usage vs in-app users' },
      { value: '3 sizes', label: 'small, medium and large, each with its own action set' },
      { value: '0 taps', label: 'to check range from the home screen' },
    ],
  },
  {
    id: 'ch-3',
    n: 2,
    num: '03',
    tag: 'EV CONTROLS',
    title: 'Charge limits and stop-charge, finally in drivers’ hands',
    body: 'Charge limit controls and stop-charge capability had been standard on competitor EVs for years. Closing that gap was essential to the ownership experience — drivers gained real control over how and when their vehicle charges, plus scheduling that keeps charging inside off-peak hours.',
    stats: [
      { value: '+20%', label: 'EV feature engagement after release' },
      { value: 'Years', label: 'of a competitive gap closed in one release' },
      { value: 'Off-peak', label: 'scheduling built in from day one' },
    ],
  },
  {
    id: 'ch-4',
    n: 3,
    num: '04',
    tag: 'DRIVING ANALYSIS',
    title: 'A dashboard that explains the range you actually get',
    body: 'The driving analysis dashboard turns trip telemetry into something a driver can act on: an efficiency score, weekly consumption trends, regen recovery, and plain-language nudges about the habits that cost range. It gave EV owners the transparency they had been asking for in reviews and support tickets.',
    stats: [
      { value: '7-day', label: 'efficiency trend at a glance' },
      { value: 'kWh/100km', label: 'consumption made legible to non-engineers' },
      { value: 'Plain words', label: 'every insight written as a sentence, not a chart' },
    ],
  },
];

const ProductConsumerPage = () => {
  const [active, setActive] = useState(0);
  const stackRef = useRef(null);
  const phoneRef = useRef(null);
  const boxRef = useRef(null);

  const fit = useCallback(() => {
    const p = phoneRef.current;
    const b = boxRef.current;
    if (!p) return;
    const avail = window.innerHeight - 132;
    const wAvail = b ? b.clientWidth : 333;
    const s = Math.min(1, avail / 682, wAvail / 333);
    p.style.transform = `scale(${s})`;
    p.style.marginBottom = `${682 * s - 682}px`;
  }, []);

  useEffect(() => {
    const el = stackRef.current;
    if (el) el.style.transform = `translateY(-${active * 100}%)`;
  }, [active]);

  useEffect(() => {
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [fit]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const n = parseInt(e.target.dataset.chapter, 10);
            if (!Number.isNaN(n)) setActive(n);
          }
        });
      },
      { threshold: 0.4, rootMargin: '-20% 0px -30% 0px' }
    );
    document.querySelectorAll('[data-chapter]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const jump = (i) => (ev) => {
    ev.preventDefault();
    const el = document.getElementById(SECTIONS[i].id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
    setActive(i);
  };

  const glow = GLOWS[active] || GLOWS[0];

  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: FONT_BODY, color: INK }}>
      <style>{`
        @keyframes caPulse { 0%,100% { opacity: .35; } 50% { opacity: .9; } }
        @keyframes caSweep { from { transform: translateX(-100%); } to { transform: translateX(220%); } }
        @keyframes caBob { 0%,100% { transform: translateY(0) rotate(-.5deg); } 50% { transform: translateY(-5px) rotate(.5deg); } }
        @keyframes caRoll { to { transform: rotate(360deg); } }
        @keyframes caDash { to { background-position-x: -56px; } }
        @keyframes caBlink { 0%,92%,100% { transform: scaleY(1); } 96% { transform: scaleY(.12); } }
        @keyframes caChomp { 0%,60%,100% { transform: rotate(0deg); } 30% { transform: rotate(-15deg); } }
        .ca-chapter-link:hover span:last-child { color: ${INK} !important; }
      `}</style>

      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '26px 20px 0' }}>
        {/* Top bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 0 12px',
          }}
        >
          <BackButton color="rgba(17,16,16,.55)" hoverColor={INK} fontFamily="'Outfit', sans-serif" />
          <div
            style={{
              font: "500 10.5px 'JetBrains Mono', monospace",
              letterSpacing: '.16em',
              color: 'rgba(17,16,16,.4)',
            }}
          >
            PRODUCT / CONSUMER APPS · {SECTIONS.length} CHAPTERS
          </div>
        </div>

        {/* Hero */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 40,
            flexWrap: 'wrap',
            padding: '18px 0 40px',
          }}
        >
          <h1
            style={{
              margin: 0,
              font: "900 clamp(46px,7.6vw,116px)/.86 'Archivo', sans-serif",
              letterSpacing: '-.035em',
              textTransform: 'uppercase',
            }}
          >
            Consumer
            <br />
            Apps
          </h1>
          <p
            style={{
              margin: '0 0 10px',
              font: "400 13.5px/1.62 'Outfit', sans-serif",
              color: 'rgba(17,16,16,.55)',
              maxWidth: 330,
            }}
          >
            Mobile features for over a million drivers, shipped across iOS and Android. Scroll the story — the phone
            follows along. Tap a chapter marker to jump.
          </p>
        </div>

        {/* Main grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(340px,420px) 1fr', gap: 56 }}>
          {/* Left: chapter markers + phone */}
          <div style={{ alignSelf: 'stretch' }}>
            <div style={{ position: 'sticky', top: 88, display: 'flex', gap: 22, alignItems: 'flex-start', paddingBottom: 40 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 26 }}>
                {SECTIONS.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={jump(i)}
                    className="ca-chapter-link"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '26px auto',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 0',
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                  >
                    <span
                      style={{
                        height: 2,
                        width: i === active ? 26 : 10,
                        background: i === active ? PALETTE[i] : 'rgba(17,16,16,.22)',
                        borderRadius: 2,
                        transition: 'width .35s ease, background .35s ease',
                      }}
                    />
                    <span
                      style={{
                        font: "500 9.5px 'JetBrains Mono', monospace",
                        letterSpacing: '.16em',
                        color: i === active ? INK : 'rgba(17,16,16,.3)',
                        writingMode: 'vertical-rl',
                        transition: 'color .35s ease',
                      }}
                    >
                      {s.tag}
                    </span>
                  </a>
                ))}
              </div>

              <div ref={boxRef} style={{ position: 'relative', flex: 1, display: 'flex', justifyContent: 'center' }}>
                <div ref={phoneRef} style={{ position: 'relative', width: 333, height: 682, flex: 'none', transformOrigin: 'top center' }}>
                  <div
                    style={{
                      position: 'absolute',
                      inset: '-26px -18px -34px',
                      borderRadius: 64,
                      background: glow,
                      filter: 'blur(46px)',
                      opacity: 0.5,
                      transition: 'background .7s ease',
                    }}
                  />
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      background: '#0C0C0D',
                      borderRadius: 52,
                      padding: 11,
                      boxShadow: '0 44px 80px -34px rgba(17,16,16,.62), 0 0 0 1.5px rgba(255,255,255,.14) inset',
                    }}
                  >
                    <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: 42, overflow: 'hidden', background: '#F6F3EE' }}>
                      <div ref={stackRef} style={{ position: 'absolute', inset: 0, transition: 'transform 760ms cubic-bezier(.45,.02,.15,1)' }}>

                        {/* Screen 1: illustrated home scene */}
                        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: 'linear-gradient(178deg,#B9CDF0 0%,#DCD3F0 44%,#F7D9C6 78%,#F6C9B4 100%)' }}>
                          <div style={{ position: 'absolute', left: 112, top: 186, width: 84, height: 84, borderRadius: '50%', background: 'radial-gradient(circle at 40% 38%,#FFF2CE,#F6C98F)', boxShadow: '0 0 44px 16px rgba(246,201,143,.5)' }} />
                          <div style={{ position: 'absolute', left: -56, bottom: 150, width: 250, height: 150, borderRadius: '50%', background: 'rgba(140,160,205,.32)' }} />
                          <div style={{ position: 'absolute', right: -70, bottom: 158, width: 230, height: 126, borderRadius: '50%', background: 'rgba(150,140,195,.28)' }} />

                          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 158, background: 'linear-gradient(180deg,#2B3550,#1B2135)' }} />
                          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 96, height: 3, background: 'repeating-linear-gradient(90deg,rgba(255,255,255,.5) 0 28px,transparent 28px 56px)', animation: 'caDash .8s linear infinite' }} />

                          <div style={{ position: 'absolute', left: 22, bottom: 242, width: 52, height: 3, borderRadius: 2, background: 'rgba(255,255,255,.85)', animation: 'caSweep 1.5s linear infinite' }} />
                          <div style={{ position: 'absolute', left: 14, bottom: 284, width: 34, height: 3, borderRadius: 2, background: 'rgba(255,255,255,.7)', animation: 'caSweep 2s linear infinite .35s' }} />

                          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 118, display: 'flex', justifyContent: 'center', zIndex: 4 }}>
                            <div style={{ position: 'relative', width: 262, height: 172, animation: 'caBob 2.6s ease-in-out infinite', transformOrigin: '50% 94%' }}>
                              <div style={{ position: 'absolute', left: 8, bottom: 16, width: 246, height: 52, borderRadius: '15px 20px 12px 12px', background: 'linear-gradient(180deg,#F0F2F7,#C9D2E2)', boxShadow: '0 20px 30px -16px rgba(17,16,16,.55)' }} />
                              <div style={{ position: 'absolute', left: 54, bottom: 60, width: 150, height: 52, borderRadius: '24px 30px 6px 6px', background: 'linear-gradient(180deg,#FBFCFE,#DCE3EF)' }} />
                              <div style={{ position: 'absolute', left: 66, bottom: 66, width: 60, height: 38, borderRadius: '16px 5px 4px 4px', background: 'linear-gradient(160deg,#8FB2E0,#5C7FE0)' }} />
                              <div style={{ position: 'absolute', left: 134, bottom: 66, width: 58, height: 38, borderRadius: '5px 18px 4px 4px', background: 'linear-gradient(160deg,#9CBCE6,#6C8DE4)' }} />
                              <div style={{ position: 'absolute', left: 246, bottom: 44, width: 14, height: 9, borderRadius: 3, background: '#FFF2CE', boxShadow: '0 0 18px 6px rgba(255,242,206,.75)' }} />
                              <div style={{ position: 'absolute', left: 4, bottom: 42, width: 11, height: 8, borderRadius: 3, background: '#E4948A' }} />
                              <div style={{ position: 'absolute', left: 38, bottom: 0, width: 44, height: 44, borderRadius: '50%', background: '#15161A', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'caRoll .7s linear infinite' }}>
                                <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#C9CDD6', boxShadow: '0 0 0 3px #8E949F inset' }} />
                              </div>
                              <div style={{ position: 'absolute', left: 180, bottom: 0, width: 44, height: 44, borderRadius: '50%', background: '#15161A', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'caRoll .7s linear infinite' }}>
                                <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#C9CDD6', boxShadow: '0 0 0 3px #8E949F inset' }} />
                              </div>

                              <div style={{ position: 'absolute', left: 70, bottom: 70, width: 52, height: 44, zIndex: 3 }}>
                                <div style={{ position: 'absolute', left: 24, top: -11, width: 2.5, height: 13, background: '#4FC7A5', borderRadius: 2 }} />
                                <div style={{ position: 'absolute', left: 20, top: -18, width: 10, height: 10, borderRadius: '50%', background: '#5FD8B4', animation: 'caPulse 1.6s ease-in-out infinite' }} />
                                <div style={{ position: 'absolute', left: 2, top: 0, width: 46, height: 40, borderRadius: '17px 17px 12px 12px', background: 'linear-gradient(165deg,#7FE3C4,#41BE9B)' }}>
                                  <div style={{ position: 'absolute', left: 9, top: 14, width: 9, height: 9, borderRadius: '50%', background: '#10262B', animation: 'caBlink 3.4s ease-in-out infinite' }} />
                                  <div style={{ position: 'absolute', left: 27, top: 14, width: 9, height: 9, borderRadius: '50%', background: '#10262B', animation: 'caBlink 3.4s ease-in-out infinite' }} />
                                  <div style={{ position: 'absolute', left: 15, top: 28, width: 16, height: 6, borderRadius: '0 0 9px 9px', background: '#10262B' }} />
                                </div>
                              </div>

                              <div style={{ position: 'absolute', left: 126, bottom: 104, width: 104, height: 68, zIndex: 5, animation: 'caChomp 2.8s ease-in-out infinite', transformOrigin: '6% 92%' }}>
                                <div style={{ position: 'absolute', left: 0, bottom: 2, width: 46, height: 9, borderRadius: 5, background: '#41BE9B', transform: 'rotate(-32deg)', transformOrigin: '0 50%' }} />
                                <div style={{ position: 'absolute', left: 38, top: 14, width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(150deg,#F4A79D,#DC7268)' }} />
                                <div style={{ position: 'absolute', left: 68, top: 10, width: 22, height: 22, borderRadius: '50%', background: '#DCD3F0' }} />
                                <div style={{ position: 'absolute', left: 56, top: 6, width: 3, height: 12, borderRadius: 2, background: '#6B7A46', transform: 'rotate(16deg)' }} />
                                <div style={{ position: 'absolute', left: 59, top: 6, width: 14, height: 9, borderRadius: '70% 10% 70% 10%', background: '#7E9455' }} />
                              </div>
                            </div>
                          </div>

                          <div style={{ position: 'absolute', left: '50%', bottom: 14, transform: 'translateX(-50%)', width: 118, height: 4, borderRadius: 2, background: 'rgba(255,255,255,.55)' }} />
                        </div>

                        {/* Screen 2: vehicle status widgets */}
                        <div style={{ position: 'relative', width: '100%', height: '100%', background: 'linear-gradient(160deg,#B8CBEA 0%,#CFC3E8 52%,#F0C7C0 100%)', padding: '44px 18px 22px', display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', font: "500 11px 'Outfit', sans-serif", color: 'rgba(17,16,16,.75)', padding: '0 6px' }}>
                            <span>9:41</span>
                            <span>▮▮▮ ⌁</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 22 }}>
                            <div style={{ gridColumn: '1 / -1', background: 'rgba(255,255,255,.9)', borderRadius: 22, padding: '15px 16px', boxShadow: '0 12px 26px -16px rgba(17,16,16,.5)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.14em', color: 'rgba(17,16,16,.45)' }}>
                                <span>VEHICLE · LARGE</span>
                                <span>3 MIN AGO</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 12 }}>
                                <div>
                                  <div style={{ font: "800 34px/1 'Archivo', sans-serif", letterSpacing: '-.03em' }}>78%</div>
                                  <div style={{ font: "400 11.5px 'Outfit', sans-serif", color: 'rgba(17,16,16,.55)', marginTop: 3 }}>412 km · charging paused</div>
                                </div>
                                <div style={{ display: 'flex', gap: 7 }}>
                                  <div style={{ width: 38, height: 38, borderRadius: 12, background: INK, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', font: "500 7.5px 'JetBrains Mono', monospace" }}>LOCK</div>
                                  <div style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(17,16,16,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: "500 7.5px 'JetBrains Mono', monospace", color: 'rgba(17,16,16,.6)' }}>21°</div>
                                </div>
                              </div>
                              <div style={{ height: 6, borderRadius: 3, background: 'rgba(17,16,16,.1)', marginTop: 14, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: '78%', borderRadius: 3, background: 'linear-gradient(90deg,#7FB8EA,#5C7FE0)' }} />
                              </div>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,.9)', borderRadius: 20, padding: 14, boxShadow: '0 12px 26px -16px rgba(17,16,16,.5)', aspectRatio: '1', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                              <div style={{ font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.14em', color: 'rgba(17,16,16,.45)' }}>CLIMATE</div>
                              <div>
                                <div style={{ font: "800 26px/1 'Archivo', sans-serif", letterSpacing: '-.03em' }}>21°</div>
                                <div style={{ font: "400 11px 'Outfit', sans-serif", color: 'rgba(17,16,16,.5)', marginTop: 2 }}>Preconditioning</div>
                              </div>
                              <div style={{ height: 30, borderRadius: 15, background: INK, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.1em' }}>START</div>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,.9)', borderRadius: 20, padding: 14, boxShadow: '0 12px 26px -16px rgba(17,16,16,.5)', aspectRatio: '1', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                              <div style={{ font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.14em', color: 'rgba(17,16,16,.45)' }}>LOCKS</div>
                              <div>
                                <div style={{ font: "800 20px/1.05 'Archivo', sans-serif", letterSpacing: '-.02em' }}>All<br />locked</div>
                                <div style={{ font: "400 11px 'Outfit', sans-serif", color: 'rgba(17,16,16,.5)', marginTop: 4 }}>Windows up</div>
                              </div>
                              <div style={{ height: 30, borderRadius: 15, border: '1px solid rgba(17,16,16,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.1em', color: 'rgba(17,16,16,.65)' }}>UNLOCK</div>
                            </div>
                          </div>
                          <div style={{ flex: 1 }} />
                          <div style={{ display: 'flex', gap: 5, justifyContent: 'center', marginBottom: 14 }}>
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(17,16,16,.55)' }} />
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(17,16,16,.22)' }} />
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(17,16,16,.22)' }} />
                          </div>
                          <div style={{ width: 118, height: 4, borderRadius: 2, background: 'rgba(17,16,16,.45)', margin: '0 auto' }} />
                        </div>

                        {/* Screen 3: charging ring */}
                        <div style={{ position: 'relative', width: '100%', height: '100%', background: '#FFFDF7', padding: '42px 20px 22px', display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', font: "500 11px 'Outfit', sans-serif", color: 'rgba(17,16,16,.7)' }}>
                            <span>9:41</span>
                            <span>▮▮▮ ⌁</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
                            <span style={{ font: "800 21px/1 'Archivo', sans-serif", letterSpacing: '-.025em' }}>Charging</span>
                            <span style={{ font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.14em', color: '#2E9E6B', background: 'rgba(46,158,107,.12)', padding: '5px 9px', borderRadius: 999 }}>ACTIVE</span>
                          </div>
                          <div style={{ position: 'relative', margin: '22px auto 0', width: 176, height: 176, borderRadius: '50%', background: 'conic-gradient(#5C7FE0 0turn .78turn, rgba(17,16,16,.09) .78turn 1turn)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ position: 'absolute', inset: 15, borderRadius: '50%', background: '#FFFDF7' }} />
                            <div style={{ position: 'relative', textAlign: 'center' }}>
                              <div style={{ font: "800 44px/1 'Archivo', sans-serif", letterSpacing: '-.04em' }}>78<span style={{ fontSize: 22 }}>%</span></div>
                              <div style={{ font: "400 11.5px 'Outfit', sans-serif", color: 'rgba(17,16,16,.5)', marginTop: 4 }}>412 km · 1h 12m left</div>
                            </div>
                          </div>
                          <div style={{ marginTop: 24 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', font: "500 9px 'JetBrains Mono', monospace", letterSpacing: '.14em', color: 'rgba(17,16,16,.45)' }}>
                              <span>CHARGE LIMIT</span>
                              <span style={{ color: INK }}>80%</span>
                            </div>
                            <div style={{ position: 'relative', height: 8, borderRadius: 4, background: 'rgba(17,16,16,.1)', marginTop: 11 }}>
                              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '80%', borderRadius: 4, background: 'linear-gradient(90deg,#8CEBD8,#5C7FE0)' }} />
                              <div style={{ position: 'absolute', left: '80%', top: '50%', width: 24, height: 24, margin: '-12px 0 0 -12px', borderRadius: '50%', background: '#FFFDF7', boxShadow: '0 3px 10px rgba(17,16,16,.32), 0 0 0 1px rgba(17,16,16,.1)' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 9, font: "400 10.5px 'Outfit', sans-serif", color: 'rgba(17,16,16,.4)' }}>
                              <span>50%</span>
                              <span>Daily driving</span>
                              <span>100%</span>
                            </div>
                          </div>
                          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 9 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid rgba(17,16,16,.1)', borderRadius: 14, padding: '12px 14px' }}>
                              <span style={{ font: "400 13px 'Outfit', sans-serif" }}>Scheduled departure</span>
                              <span style={{ font: "500 12px 'Outfit', sans-serif", color: 'rgba(17,16,16,.5)' }}>7:30 AM</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid rgba(17,16,16,.1)', borderRadius: 14, padding: '12px 14px' }}>
                              <span style={{ font: "400 13px 'Outfit', sans-serif" }}>Off-peak charging</span>
                              <span style={{ width: 38, height: 22, borderRadius: 11, background: '#5C7FE0', position: 'relative' }}>
                                <span style={{ position: 'absolute', right: 2, top: 2, width: 18, height: 18, borderRadius: '50%', background: '#fff' }} />
                              </span>
                            </div>
                          </div>
                          <div style={{ flex: 1 }} />
                          <div style={{ height: 48, borderRadius: 24, background: INK, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', font: "500 11px 'JetBrains Mono', monospace", letterSpacing: '.16em' }}>STOP CHARGING</div>
                          <div style={{ width: 118, height: 4, borderRadius: 2, background: 'rgba(17,16,16,.45)', margin: '14px auto 0' }} />
                        </div>

                        {/* Screen 4: driving analysis */}
                        <div style={{ position: 'relative', width: '100%', height: '100%', background: '#FFFDF7', padding: '42px 20px 22px', display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', font: "500 11px 'Outfit', sans-serif", color: 'rgba(17,16,16,.7)' }}>
                            <span>9:41</span>
                            <span>▮▮▮ ⌁</span>
                          </div>
                          <div style={{ marginTop: 20 }}>
                            <div style={{ font: "500 9px 'JetBrains Mono', monospace", letterSpacing: '.16em', color: 'rgba(17,16,16,.42)' }}>LAST 7 DAYS</div>
                            <div style={{ font: "800 21px/1 'Archivo', sans-serif", letterSpacing: '-.025em', marginTop: 7 }}>Driving analysis</div>
                          </div>
                          <div style={{ marginTop: 18, borderRadius: 20, background: 'linear-gradient(150deg,#1B2135,#2B3550)', padding: '18px 18px 16px', color: '#fff' }}>
                            <div style={{ font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.16em', opacity: 0.6 }}>EFFICIENCY SCORE</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 9 }}>
                              <span style={{ font: "800 42px/1 'Archivo', sans-serif", letterSpacing: '-.04em' }}>92</span>
                              <span style={{ font: "400 12px 'Outfit', sans-serif", opacity: 0.6 }}>+6 vs last week</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 58, marginTop: 16 }}>
                              <div style={{ flex: 1, height: '44%', borderRadius: 4, background: 'rgba(255,255,255,.24)' }} />
                              <div style={{ flex: 1, height: '62%', borderRadius: 4, background: 'rgba(255,255,255,.24)' }} />
                              <div style={{ flex: 1, height: '38%', borderRadius: 4, background: 'rgba(255,255,255,.24)' }} />
                              <div style={{ flex: 1, height: '75%', borderRadius: 4, background: 'rgba(255,255,255,.24)' }} />
                              <div style={{ flex: 1, height: '58%', borderRadius: 4, background: 'rgba(255,255,255,.24)' }} />
                              <div style={{ flex: 1, height: '88%', borderRadius: 4, background: 'linear-gradient(180deg,#8CEBD8,#7FB8EA)' }} />
                              <div style={{ flex: 1, height: '66%', borderRadius: 4, background: 'rgba(255,255,255,.24)' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, font: "400 9px 'JetBrains Mono', monospace", letterSpacing: '.1em', opacity: 0.45 }}>
                              <span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
                            <div style={{ border: '1px solid rgba(17,16,16,.1)', borderRadius: 16, padding: '13px 14px' }}>
                              <div style={{ font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.14em', color: 'rgba(17,16,16,.42)' }}>CONSUMPTION</div>
                              <div style={{ font: "800 22px/1 'Archivo', sans-serif", letterSpacing: '-.03em', marginTop: 8 }}>16.4</div>
                              <div style={{ font: "400 11px 'Outfit', sans-serif", color: 'rgba(17,16,16,.5)', marginTop: 3 }}>kWh / 100 km</div>
                            </div>
                            <div style={{ border: '1px solid rgba(17,16,16,.1)', borderRadius: 16, padding: '13px 14px' }}>
                              <div style={{ font: "500 8.5px 'JetBrains Mono', monospace", letterSpacing: '.14em', color: 'rgba(17,16,16,.42)' }}>REGEN</div>
                              <div style={{ font: "800 22px/1 'Archivo', sans-serif", letterSpacing: '-.03em', marginTop: 8 }}>31%</div>
                              <div style={{ font: "400 11px 'Outfit', sans-serif", color: 'rgba(17,16,16,.5)', marginTop: 3 }}>of energy recovered</div>
                            </div>
                          </div>
                          <div style={{ marginTop: 12, borderRadius: 16, background: 'rgba(92,127,224,.1)', padding: '13px 14px' }}>
                            <div style={{ font: "400 12.5px/1.45 'Outfit', sans-serif", color: 'rgba(17,16,16,.72)' }}>
                              Smoother acceleration on Saturday saved you an estimated <strong>18 km</strong> of range.
                            </div>
                          </div>
                          <div style={{ flex: 1 }} />
                          <div style={{ width: 118, height: 4, borderRadius: 2, background: 'rgba(17,16,16,.45)', margin: '0 auto' }} />
                        </div>

                      </div>
                      <div style={{ position: 'absolute', top: 11, left: '50%', transform: 'translateX(-50%)', width: 104, height: 29, background: '#0C0C0D', borderRadius: 16, zIndex: 5 }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: chapter copy */}
          <div>
            {SECTIONS.map((s, i) => (
              <section
                key={s.id}
                id={s.id}
                data-chapter={s.n}
                style={{ minHeight: '92vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 0', borderTop: '1px solid rgba(17,16,16,.1)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, font: "500 10px 'JetBrains Mono', monospace", letterSpacing: '.18em', color: 'rgba(17,16,16,.42)' }}>
                  <span style={{ color: PALETTE[i] }}>{s.num}</span>
                  <span>{s.tag}</span>
                </div>
                <h2
                  style={{
                    margin: '18px 0 0',
                    font: "800 clamp(30px,3.5vw,50px)/1.02 'Archivo', sans-serif",
                    letterSpacing: '-.032em',
                    maxWidth: '16ch',
                  }}
                >
                  {s.title}
                </h2>
                <p style={{ margin: '20px 0 0', font: "300 17.5px/1.62 'Outfit', sans-serif", color: 'rgba(17,16,16,.72)', maxWidth: '56ch' }}>
                  {s.body}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 34 }}>
                  {s.stats.map((st) => (
                    <div key={st.label} style={{ minWidth: 168, flex: '1 1 168px', borderTop: `2px solid ${PALETTE[i]}`, padding: '14px 0 0' }}>
                      <div style={{ font: "800 clamp(30px,3vw,42px)/.95 'Archivo', sans-serif", letterSpacing: '-.04em' }}>{st.value}</div>
                      <div style={{ font: "400 12.5px/1.45 'Outfit', sans-serif", color: 'rgba(17,16,16,.5)', marginTop: 9, maxWidth: '22ch' }}>{st.label}</div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
            <div style={{ height: '24vh' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductConsumerPage;
